import { faCopy } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import { useOffer } from '../hooks/useOffer'
import offerService from '../services/offerService'
import { NewOfferTDO } from '../types/offers'
import { FILE_SIZE, OfferRedemptionType, OfferType, SUPPORTED_FORMATS } from '../utils/config'
import { queryClient } from '../utils/react-query'

import Card from '../components/Card'
import DeleteButton from '../components/DeleteButton'
import OfferError from '../components/Iffer/OfferError'
import OfferForm from '../components/Offers/OfferForm'
import Spinner from '../components/Spinner'
import { useUser } from '../hooks/useUser'
import { BuzzlUsersError } from '../types/buzzl-user'
import { convertToDayPassed } from '../utils/utils'

type OfferFormType = NewOfferTDO & { id: string | null }

export const schema = Yup.object().shape({
  type: Yup.string().oneOf([OfferType.offer, OfferType.reward]),
  title: Yup.string().required().label('Title'),
  customer: Yup.object()
    .shape({
      id: Yup.string().required(),
    })
    .label('Customer')
    .required(),
  places: Yup.array().of(Yup.string()),
  categories: Yup.string().required().label('Category'),
  image: Yup.mixed()
    .test('fileSize', 'File too large', (value) => !value || (value && value.size <= FILE_SIZE))
    .test('fileFormat', 'Unsupported Format', (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type)))
    .when(['id', 'type'], {
      is: (id: string | null, type: OfferType) => !id && type === OfferType.reward,
      then: (schema) => schema.required(),
    })
    .label('Image'),
  termsAndConditions: Yup.string().required().label('Terms and Conditions'),
  howToRedeem: Yup.string().required().label('How to redeem'),
  activeMessage: Yup.string().label('Active message').when('type', {
    is: OfferType.reward,
    then: Yup.string().required(),
  }),
  earnableFrom: Yup.date().required().label('Earn From'),
  earnableTo: Yup.date()
    .when('earnableFrom', (earnableFrom, schema) => earnableFrom && schema.min(earnableFrom))
    .required()
    .label('Earn Until'),
  redeemableFrom: Yup.date().required().label('Redemption From'),
  redeemableTo: Yup.date()
    .when('redeemableFrom', (redeemableFrom, schema) => redeemableFrom && schema.min(redeemableFrom))
    .required()
    .label('Redemption Until'),
  description: Yup.string().when('type', {
    is: OfferType.reward,
    then: (schema) => schema.required(),
  }),
  cost: Yup.number(),
  maxClaimable: Yup.number().min(1),
  nbPermittedClaims: Yup.number().min(1),
  qrCode: Yup.mixed()
    .test('fileSize', 'File too large', (value) => !value || (value && value.size <= FILE_SIZE))
    .test('fileFormat', 'Unsupported Format', (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))),
  redemptionType: Yup.string().oneOf(Object.values(OfferRedemptionType)),
  onlineRedemptionUrl: Yup.string().url().label('Online Redemption URL'),
  onlineRedemptionCode: Yup.string().label('Online Redemption Discount Code'),
})

const NewOffer = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const user = useUser()
  const { data: offer, isLoading } = useOffer(id)

  const initialValues: OfferFormType = {
    id: offer?.id ?? null,
    type: offer?.type || OfferType.reward,
    redemptionType: offer?.redemptionType || OfferRedemptionType.InPerson,
    title: offer?.title || '',
    customer: user.data?.type === 'CUSTOMER' ? user.data?.customerUser?.customer : offer?.customer || undefined,
    places: offer?.places || [],
    categories: offer?.categories || '',
    image: undefined,
    termsAndConditions: offer?.termsAndConditions || '',
    howToRedeem: offer?.howToRedeem || '',
    activeMessage: offer?.activeMessage || '',
    description: offer?.description || '',
    cost: offer?.cost || 0,
    maxClaimable: offer?.maxClaimable || '',
    qrCode: undefined,
    earnableFrom: offer?.earnableFrom || '',
    earnableTo: offer?.earnableTo || '',
    redeemableFrom: offer?.redeemableFrom || '',
    redeemableTo: offer?.redeemableTo || '',
    nbPermittedClaims: offer?.nbPermittedClaims || '',
    onlineRedemptionUrl: offer?.onlineRedemptionUrl || '',
    onlineRedemptionCode: offer?.onlineRedemptionCode || '',
  }

  const createOffer = useMutation(offerService.createOffer, {
    onSuccess: async () => {
      queryClient.refetchQueries(['offers'])
      navigate('/rewards-and-offers')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const updateOffer = useMutation(offerService.updateOffer, {
    onSuccess: async () => {
      queryClient.refetchQueries(['offers'])
      queryClient.refetchQueries(['offer', offer?.id])
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const deleteOffer = useMutation(() => offerService.deleteById(id || ''), {
    onSuccess: async () => {
      await queryClient.removeQueries(['offers'])
      toast.success('Offer has been deleted.')
      navigate('/rewards-and-offers')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const copyOffer = useMutation(offerService.copyOffer, {
    onSuccess: async () => {
      queryClient.refetchQueries(['offers'])
      navigate('/rewards-and-offers')
    },
  })

  const handleOnSubmit = async (
    {
      id,
      image,
      qrCode,
      earnableFrom,
      earnableTo,
      redeemableFrom,
      redeemableTo,
      nbPermittedClaims,
      maxClaimable,
      ...rest
    }: OfferFormType,
    formikBag: FormikHelpers<OfferFormType>
  ) => {
    if (rest.type === OfferType.reward && !rest.activeMessage) {
      formikBag.setFieldError('activeMessage', 'Active Reward Message is required.')
    }

    const onlineRedemptionCode =
      rest.onlineRedemptionCode && rest.redemptionType === 'ONLINE' ? rest.onlineRedemptionCode : null
    const onlineRedemptionUrl =
      rest.onlineRedemptionUrl && rest.redemptionType === 'ONLINE' ? rest.onlineRedemptionUrl : null

    const obj: any = {
      ...rest,
      onlineRedemptionCode,
      onlineRedemptionUrl,
    }

    try {
      if (image) {
        obj.image = await offerService.uploadImage({
          customerId: rest.customer?.id,
          file: image,
          offerId: offer?.id,
        })
      }

      if (qrCode) {
        obj.qrCode = await offerService.uploadQr({
          customerId: rest.customer?.id,
          file: qrCode,
          offerId: offer?.id,
        })
      }

      if (id) {
        await toast.promise(
          updateOffer.mutateAsync({
            ...obj,
            nbPermittedClaims: nbPermittedClaims ? nbPermittedClaims : null,
            maxClaimable: maxClaimable ? maxClaimable : null,
            earnableFrom,
            earnableTo: new Date(convertToDayPassed(earnableTo)).toISOString().split('T')[0],
            redeemableFrom,
            redeemableTo: new Date(convertToDayPassed(redeemableTo)).toISOString().split('T')[0],
            id,
          }),
          {
            loading: 'Updating...',
            success: 'Updated successfully!',
            error: 'Could not update. Try again',
          }
        )
      } else {
        await toast.promise(
          createOffer.mutateAsync({
            ...obj,
            nbPermittedClaims: nbPermittedClaims ? nbPermittedClaims : null,
            maxClaimable: maxClaimable ? maxClaimable : null,
            earnableFrom,
            earnableTo: new Date(convertToDayPassed(earnableTo)).toISOString().split('T')[0],
            redeemableFrom,
            redeemableTo: new Date(convertToDayPassed(redeemableTo)).toISOString().split('T')[0],
          }),
          {
            loading: 'Creating...',
            success: 'Your offer has been created!',
            error: 'Could not create. Try again!',
          }
        )
      }
    } catch (error) {
      console.log('🚀 ~ file: NewOffer.tsx ~ line 85 ~ handleOnSubmit ~ error', error)
    }
  }

  const handleCopy = async (offerId: string) => {
    try {
      const { id, createdAt, ...singleOffer } = await offerService.getOne(offerId)
      await toast.promise(copyOffer.mutateAsync(singleOffer), {
        loading: 'Copying...',
        success: 'Copied successfully!',
        error: 'Could not copy. Try again',
      })
    } catch {
      toast.error('Something went wrong, if the error persist please contact support.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    )
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleOnSubmit}
    >
      {({ setFieldValue, isSubmitting, values, dirty }: FormikProps<NewOfferTDO>) => {
        return (
          <Form>
            <OfferError />
            <div className="mb-6 flex items-center justify-between">
              <p>{id ? (offer?.type === 'OFFER' ? 'Update Offer' : 'Update Reward') : 'New Reward or Offer'}</p>
              <div>
                <Link to="/rewards-and-offers" className="btn btn-white mr-5">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting || isLoading || !dirty}>
                  {id ? 'Update' : 'Create'}
                </button>
              </div>
            </div>

            <Card.Container>
              <Card.Heading>
                <Card.HeadingLeft>Overview</Card.HeadingLeft>
                {offer && offer.id ? (
                  <Card.HeadingRight>
                    <button
                      className="btn btn-white"
                      title="Duplicate"
                      onClick={() => handleCopy(offer.id)}
                      type="button"
                    >
                      <FontAwesomeIcon className="leading-icon" icon={faCopy} />
                      Duplicate
                    </button>
                  </Card.HeadingRight>
                ) : null}
              </Card.Heading>

              <OfferForm
                setFieldValue={setFieldValue}
                values={values}
                isSubmitting={isSubmitting || isLoading}
                imageUrl={offer?.image?.url}
                qrUrl={offer?.qrCode?.url}
              />
            </Card.Container>

            <div className="mb-4 flex justify-end gap-5">
              {id ? (
                <DeleteButton
                  type="button"
                  className="btn btn-delete mr-auto"
                  modalTitle="Delete a reward/offer"
                  modalText="You are about to remove a reward/offer. This is a permanent action and cannot be undone. Are you sure you want to remove this reward/offer?"
                  disabled={deleteOffer.isLoading}
                  onClick={async () => {
                    await deleteOffer.mutateAsync()
                  }}
                >
                  {deleteOffer.isLoading ? (
                    <div className="mr-3">
                      <Spinner className="trailing-icon h-4 w-4" />
                    </div>
                  ) : null}
                  Delete Offer/Reward
                </DeleteButton>
              ) : null}
              <Link to="/rewards-and-offers" className="btn btn-white">
                Cancel
              </Link>
              <button className="btn btn-primary" type="submit" disabled={isSubmitting || isLoading || !dirty}>
                {id ? 'Update' : 'Create'}
              </button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default NewOffer
